import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  ChevronLeft, ChevronRight, Lightbulb, Bot, AlertTriangle,
  CheckCircle2, PanelRightOpen, PanelRightClose, Home
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import FinancialPanel from './FinancialPanel';

const SCORE_LABELS = ['Insuffisant', 'Faible', 'Correct', 'Excellent'];
const SCORE_COLORS = [
  'bg-red-500/20 border-red-500/40 text-red-400',
  'bg-orange-500/20 border-orange-500/40 text-orange-400',
  'bg-yellow-500/20 border-yellow-500/40 text-yellow-400',
  'bg-green-500/20 border-green-500/40 text-green-400',
];

const AnalyseWizard = ({ model, analysis, setAnalysis, financialData, setFinancialData, onComplete, onSaveAndExit, onBack, apiUrl, authHeader }) => {
  const [showAdvice, setShowAdvice] = useState(null);
  const [aiLoading, setAiLoading] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [eliminatoryModal, setEliminatoryModal] = useState(null);

  const rapidNums = model.meta.mode_rapide.sections;
  const allSections = model[analysis.mode] || [];

  const activeSections = useMemo(() => {
    if (analysis.isRapid) return allSections.filter(s => rapidNums.includes(s.numero));
    return allSections;
  }, [allSections, analysis.isRapid, rapidNums]);

  const currentSection = activeSections[analysis.currentSectionIdx] || activeSections[0];

  // Fetch financial data on mount
  useEffect(() => {
    if (financialData || !analysis.ticker) return;
    (async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/market/detailed-quote/${encodeURIComponent(analysis.ticker)}`);
        setFinancialData(data);
      } catch {
        toast.error('Impossible de récupérer les données financières');
      }
    })();
  }, [analysis.ticker, financialData, setFinancialData, apiUrl]);

  // Calculate section score
  const getSectionScore = useCallback((section) => {
    if (!section) return { raw: 0, maxRaw: 0, answered: 0, total: 0 };
    let raw = 0, answered = 0;
    section.items.forEach(item => {
      const key = `${section.id}_${item.numero}`;
      if (analysis.scores[key] !== undefined) { raw += analysis.scores[key]; answered++; }
    });
    return { raw, maxRaw: section.max_pts, answered, total: section.items.length };
  }, [analysis.scores]);

  // Check eliminatory for a section (after all items answered)
  const isEliminatoryFailed = useCallback((section, scoresOverride) => {
    if (!section.eliminatoire) return false;
    const scores = scoresOverride || analysis.scores;
    let raw = 0, answered = 0;
    section.items.forEach(item => {
      const key = `${section.id}_${item.numero}`;
      if (scores[key] !== undefined) { raw += scores[key]; answered++; }
    });
    if (answered < section.items.length) return false;
    return raw < section.eliminatoire;
  }, [analysis.scores]);

  // Total progress
  const totalItems = useMemo(() => activeSections.reduce((acc, s) => acc + s.items.length, 0), [activeSections]);
  const answeredItems = useMemo(() => {
    let count = 0;
    activeSections.forEach(s => {
      s.items.forEach(item => { if (analysis.scores[`${s.id}_${item.numero}`] !== undefined) count++; });
    });
    return count;
  }, [activeSections, analysis.scores]);

  // Running weighted score
  const runningScore = useMemo(() => {
    let weighted = 0, maxWeighted = 0;
    activeSections.forEach(s => {
      const { raw, maxRaw } = getSectionScore(s);
      weighted += raw * s.poids;
      maxWeighted += maxRaw * s.poids;
    });
    return { weighted: Math.round(weighted * 10) / 10, maxWeighted: Math.round(maxWeighted * 10) / 10 };
  }, [activeSections, getSectionScore]);

  const setScore = (section, itemNumero, score) => {
    const key = `${section.id}_${itemNumero}`;
    const newScores = { ...analysis.scores, [key]: score };
    setAnalysis(prev => ({ ...prev, scores: newScores }));

    // Check eliminatory after update
    setTimeout(() => {
      if (section.eliminatoire) {
        let raw = 0, answered = 0;
        section.items.forEach(item => {
          const k = `${section.id}_${item.numero}`;
          if (newScores[k] !== undefined) { raw += newScores[k]; answered++; }
        });
        if (answered === section.items.length && raw < section.eliminatoire) {
          setEliminatoryModal({ section });
        }
      }
    }, 100);
  };

  // AI Help — backend-cached, user-scoped
  const handleAiHelp = async (section, item) => {
    const headers = authHeader ? authHeader() : {};
    const params = { ticker: analysis.ticker, section_id: section.id, item_numero: String(item.numero) };

    // 1. Try server cache first (cross-device, user-scoped)
    try {
      const r = await axios.get(`${apiUrl}/ai-cache/hint`, { params, headers });
      if (r.data?.text) {
        setAiResponse({ sectionId: section.id, itemNumero: item.numero, text: r.data.text });
        return;
      }
    } catch { /* cache miss or network — fall through to live call */ }

    const sectionUsage = analysis.aiUsage[section.id] || 0;
    if (sectionUsage >= 3) { toast.error('Maximum 3 utilisations IA par section atteint'); return; }

    setAiLoading(`${section.id}_${item.numero}`);
    try {
      const { data } = await axios.post(`${apiUrl}/ai/analyze-item`, {
        ticker: analysis.ticker,
        company_name: analysis.companyName,
        section_name: section.titre,
        item_title: item.titre,
        key_question: item.question,
        financial_data: financialData || {},
      }, { headers });
      const text = data.response;
      // Persist to backend cache (fire-and-forget)
      axios.put(`${apiUrl}/ai-cache/hint`, { ...params, text }, { headers }).catch(() => {});
      setAiResponse({ sectionId: section.id, itemNumero: item.numero, text });
      setAnalysis(prev => ({
        ...prev,
        aiUsage: { ...prev.aiUsage, [section.id]: (prev.aiUsage[section.id] || 0) + 1 }
      }));
    } catch (err) {
      toast.error('Erreur IA : ' + (err.response?.data?.detail || 'service indisponible'));
    }
    setAiLoading(null);
  };

  const goToSection = (idx) => setAnalysis(prev => ({ ...prev, currentSectionIdx: idx }));
  const nextSection = () => { if (analysis.currentSectionIdx < activeSections.length - 1) goToSection(analysis.currentSectionIdx + 1); };
  const prevSection = () => { if (analysis.currentSectionIdx > 0) goToSection(analysis.currentSectionIdx - 1); };

  const handleComplete = () => {
    const sectionScores = {};
    let totalWeighted = 0, totalMaxWeighted = 0;
    activeSections.forEach(s => {
      const { raw, maxRaw } = getSectionScore(s);
      const weighted = raw * s.poids;
      const maxW = maxRaw * s.poids;
      sectionScores[s.id] = {
        raw, maxRaw, weighted: Math.round(weighted * 10) / 10,
        maxWeighted: Math.round(maxW * 10) / 10,
        percent: maxRaw > 0 ? Math.round((raw / maxRaw) * 100) : 0,
      };
      totalWeighted += weighted;
      totalMaxWeighted += maxW;
    });
    const alerts = activeSections.filter(s => isEliminatoryFailed(s)).map(s => s.id);
    const totalPercent = totalMaxWeighted > 0 ? (totalWeighted / totalMaxWeighted) * 100 : 0;
    onComplete({
      ...analysis, sectionScores,
      totalScore: {
        weighted: Math.round(totalWeighted * 10) / 10,
        maxWeighted: Math.round(totalMaxWeighted * 10) / 10,
        percent: Math.round(totalPercent * 10) / 10,
      },
      eliminatoryAlerts: alerts,
      financialDataSnapshot: financialData,
    });
  };

  const handleSaveAndExit = () => {
    onSaveAndExit(analysis);
    toast.success('Analyse sauvegardée');
    onBack();
  };

  const sectionScore = getSectionScore(currentSection);
  const aiUsedInSection = analysis.aiUsage[currentSection?.id] || 0;

  return (
    <div className="flex h-[calc(100vh-64px)] lg:h-screen" data-testid="analyse-wizard">
      {/* Section Sidebar */}
      <div className="hidden lg:flex flex-col w-64 bg-cap-bg-subtle border-r border-white/5 overflow-y-auto flex-shrink-0">
        <div className="p-4 border-b border-white/5">
          <p className="text-cap-text-muted text-xs uppercase font-bold tracking-wider">Sections</p>
        </div>
        <div className="flex-1 py-2">
          {activeSections.map((s, idx) => {
            const sc = getSectionScore(s);
            const isCurrent = idx === analysis.currentSectionIdx;
            return (
              <button
                key={s.id}
                onClick={() => goToSection(idx)}
                data-testid={`wizard-section-${s.id}`}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                  isCurrent ? 'bg-cap-emerald/10 border-r-2 border-cap-emerald' : 'hover:bg-white/5'
                }`}
              >
                <span className={`text-xs font-mono w-6 ${isCurrent ? 'text-cap-emerald' : 'text-cap-text-muted'}`}>{s.id}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${isCurrent ? 'text-white font-medium' : 'text-cap-text-secondary'}`}>{s.titre}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-cap-emerald rounded-full transition-all" style={{ width: `${sc.total > 0 ? (sc.answered / sc.total) * 100 : 0}%` }} />
                    </div>
                    <span className="text-[10px] text-cap-text-muted">{sc.answered}/{sc.total}</span>
                  </div>
                </div>
                {s.eliminatoire && <AlertTriangle className="w-3 h-3 text-cap-warning flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-4 lg:px-6 py-3 bg-cap-bg-subtle border-b border-white/5 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={handleSaveAndExit} className="text-cap-text-secondary hover:text-white" data-testid="save-exit-btn">
            <Home className="w-4 h-4 mr-1" /> Sauvegarder & Quitter
          </Button>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-cap-text-muted">Score :</span>
            <span className="text-white font-mono font-bold">{runningScore.weighted} / {runningScore.maxWeighted}</span>
            <span className="text-cap-emerald font-mono">({runningScore.maxWeighted > 0 ? ((runningScore.weighted / runningScore.maxWeighted) * 100).toFixed(0) : 0}%)</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowPanel(!showPanel)} className="text-cap-text-secondary hover:text-white" data-testid="toggle-panel-btn">
            {showPanel ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="px-4 lg:px-6 py-2 bg-cap-bg-subtle border-b border-white/5 flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-cap-text-secondary text-sm font-medium">
              Section {analysis.currentSectionIdx + 1}/{activeSections.length} — {currentSection?.titre}
            </span>
            <span className="text-cap-text-muted text-xs">{answeredItems}/{totalItems} items</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-cap-emerald to-cap-blue rounded-full transition-all duration-300"
              style={{ width: `${totalItems > 0 ? (answeredItems / totalItems) * 100 : 0}%` }} />
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Items */}
          <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-8">
            {/* Section Header */}
            <div className="mb-6">
              <h2 className="font-display text-xl font-bold text-white mb-1">{currentSection?.titre}</h2>
              {currentSection?.eliminatoire && (() => {
                const answered = sectionScore.answered;
                const isUnderThreshold = answered > 0 && sectionScore.raw < currentSection.eliminatoire;
                if (answered === 0) {
                  return (
                    <div className="mt-3 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                      <span className="text-cap-text-secondary text-sm">
                        ℹ️ Section éliminatoire — seuil minimum requis : {currentSection.eliminatoire} pts / {currentSection.max_pts} pts
                      </span>
                    </div>
                  );
                }
                if (isUnderThreshold) {
                  return (
                    <div className="mt-3 flex items-center gap-2 bg-cap-warning/10 border border-cap-warning/20 rounded-lg px-3 py-2">
                      <AlertTriangle className="w-4 h-4 text-cap-warning flex-shrink-0" />
                      <span className="text-cap-warning text-sm font-medium">
                        ÉLIMINATOIRE si &lt; {currentSection.eliminatoire}/{currentSection.max_pts} pts — score actuel : {sectionScore.raw} pts
                      </span>
                    </div>
                  );
                }
                return (
                  <div className="mt-3 flex items-center gap-2 bg-cap-emerald/5 border border-cap-emerald/10 rounded-lg px-3 py-2">
                    <CheckCircle2 className="w-4 h-4 text-cap-emerald flex-shrink-0" />
                    <span className="text-cap-emerald/80 text-sm">
                      Section éliminatoire — seuil : {currentSection.eliminatoire}/{currentSection.max_pts} pts — score actuel : {sectionScore.raw} pts
                    </span>
                  </div>
                );
              })()}
              <div className="mt-2 flex items-center gap-2 text-xs text-cap-text-muted">
                <span>Poids : x{currentSection?.poids}</span>
                <span>|</span>
                <span>Score : {sectionScore.raw}/{sectionScore.maxRaw}</span>
                <span>|</span>
                <span>IA : {aiUsedInSection}/3</span>
              </div>
            </div>

            {/* Items */}
            {currentSection?.items.map((item, idx) => {
              const key = `${currentSection.id}_${item.numero}`;
              const selectedScore = analysis.scores[key];
              const isShowingAdvice = showAdvice === key;
              const isShowingAi = aiResponse?.sectionId === currentSection.id && aiResponse?.itemNumero === item.numero;
              const isLoadingAi = aiLoading === key;

              return (
                <div key={item.numero} className="bg-cap-bg-elevated border border-white/5 rounded-xl p-5" data-testid={`item-${key}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-cap-emerald font-mono text-sm font-bold">#{idx + 1}</span>
                        <h3 className="text-white font-semibold">{item.titre}</h3>
                      </div>
                      <p className="text-cap-text-muted text-sm">{item.utilite}</p>
                      <p className="text-cap-text-secondary text-sm italic mt-1">{item.question}</p>
                    </div>
                    {selectedScore !== undefined && <CheckCircle2 className="w-5 h-5 text-cap-emerald ml-3 flex-shrink-0" />}
                  </div>

                  {/* Score Buttons */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
                    {[0, 1, 2, 3].map(score => (
                      <button
                        key={score}
                        onClick={() => setScore(currentSection, item.numero, score)}
                        data-testid={`score-btn-${key}-${score}`}
                        className={`px-3 py-2.5 rounded-lg border text-left transition-all text-sm ${
                          selectedScore === score
                            ? SCORE_COLORS[score] + ' ring-1 ring-white/20'
                            : 'bg-cap-bg border-white/10 text-cap-text-secondary hover:border-white/20'
                        }`}
                      >
                        <span className="font-bold">{score}</span>
                        <span className="ml-1.5">{SCORE_LABELS[score]}</span>
                        <p className="text-[11px] opacity-70 mt-0.5 line-clamp-2">{item[`note_${score}`]}</p>
                      </button>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowAdvice(isShowingAdvice ? null : key)} className="text-cap-text-muted hover:text-cap-warning text-xs">
                      <Lightbulb className="w-3.5 h-3.5 mr-1" /> Conseil
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleAiHelp(currentSection, item)}
                      disabled={isLoadingAi || aiUsedInSection >= 3}
                      className="text-cap-text-muted hover:text-cap-blue text-xs"
                      data-testid={`ai-help-btn-${key}`}
                    >
                      {isLoadingAi ? (
                        <div className="w-3.5 h-3.5 border-2 border-cap-blue border-t-transparent rounded-full animate-spin mr-1" />
                      ) : (
                        <Bot className="w-3.5 h-3.5 mr-1" />
                      )}
                      Aide IA ({3 - aiUsedInSection}/3)
                    </Button>
                  </div>

                  {isShowingAdvice && (
                    <div className="mt-3 bg-cap-warning/5 border border-cap-warning/20 rounded-lg p-3">
                      <p className="text-cap-warning text-sm"><Lightbulb className="w-4 h-4 inline mr-1" />{item.conseil}</p>
                    </div>
                  )}
                  {isShowingAi && (
                    <div className="mt-3 bg-cap-blue/5 border border-cap-blue/20 rounded-lg p-3">
                      <p className="text-cap-blue text-xs font-medium mb-1"><Bot className="w-3.5 h-3.5 inline mr-1" />Aide IA</p>
                      <p className="text-cap-text-secondary text-sm whitespace-pre-line">{aiResponse.text}</p>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Navigation */}
            <div className="flex justify-between pt-4 pb-8">
              <Button variant="outline" onClick={prevSection} disabled={analysis.currentSectionIdx === 0}
                className="border-white/10 text-cap-text-secondary" data-testid="prev-section-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Section précédente
              </Button>
              {analysis.currentSectionIdx < activeSections.length - 1 ? (
                <Button onClick={nextSection} className="bg-cap-emerald hover:bg-cap-emerald-hover text-black font-bold" data-testid="next-section-btn">
                  Section suivante <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleComplete} className="bg-cap-emerald hover:bg-cap-emerald-hover text-black font-bold" data-testid="complete-analysis-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Terminer l'analyse
                </Button>
              )}
            </div>
          </div>

          {/* Financial Panel */}
          {showPanel && <FinancialPanel data={financialData} onClose={() => setShowPanel(false)} />}
        </div>
      </div>

      {/* Eliminatory Modal */}
      <Dialog open={!!eliminatoryModal} onOpenChange={() => setEliminatoryModal(null)}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cap-error flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Score insuffisant — Analyse stoppée recommandée
            </DialogTitle>
          </DialogHeader>
          {eliminatoryModal && (
            <div className="space-y-4">
              <p className="text-cap-text-secondary">
                La section <strong className="text-white">{eliminatoryModal.section.titre}</strong> n'atteint pas le seuil minimum
                de <strong className="text-cap-warning">{eliminatoryModal.section.eliminatoire}/{eliminatoryModal.section.max_pts}</strong> points.
              </p>
              <p className="text-cap-text-muted text-sm">
                Ce critère est éliminatoire car il représente un fondamental essentiel.
                Un score insuffisant ici indique un risque majeur pour votre investissement.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setEliminatoryModal(null); onBack(); }}
                  className="border-cap-error/30 text-cap-error hover:bg-cap-error/10 flex-1" data-testid="abandon-analysis-btn">
                  Abandonner l'analyse
                </Button>
                <Button variant="ghost" onClick={() => setEliminatoryModal(null)}
                  className="text-cap-text-muted flex-1" data-testid="continue-anyway-btn">
                  Continuer quand meme
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnalyseWizard;
